import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useCampaignTimezoneCatalog } from "@/hooks/useCampaignTimezoneCatalog";

const EMPTY_VALUE = "__none__";

function TimezoneOptionLabel({ name, gmt }) {
  return (
    <span className="block min-w-0 truncate whitespace-nowrap text-left text-[13px] leading-snug">
      <span className="text-slate-900">{name}</span>
      {gmt ? (
        <>
          <span className="font-normal text-slate-400"> · </span>
          <span className="text-[11px] font-normal text-slate-400">{gmt}</span>
        </>
      ) : null}
    </span>
  );
}

TimezoneOptionLabel.propTypes = {
  name: PropTypes.string.isRequired,
  gmt: PropTypes.string,
};

export function TimezoneSelect({
  value,
  onChange,
  allowEmpty = false,
  disabled = false,
  id,
  testId,
  triggerClassName,
  placeholder = "No timezone selected",
}) {
  const { catalog, loading, error, displayFor, ensureDisplay } = useCampaignTimezoneCatalog();
  const zones = catalog?.zones || [];
  const selected = displayFor(value);
  const selectValue = value || (allowEmpty ? EMPTY_VALUE : undefined);
  const blocked = disabled || loading || Boolean(error);

  useEffect(() => {
    if (!value || selected || loading) return;
    ensureDisplay(value);
  }, [value, selected, loading, ensureDisplay]);

  const handleChange = (next) => {
    onChange(next === EMPTY_VALUE ? "" : next);
  };

  let triggerContent;
  if (loading) {
    triggerContent = <span className="text-[13px] text-slate-500">Loading timezones…</span>;
  } else if (error) {
    triggerContent = <span className="text-[13px] text-rose-600">{error}</span>;
  } else if (selected) {
    triggerContent = <TimezoneOptionLabel name={selected.name} gmt={selected.gmt} />;
  } else {
    triggerContent = <span className="text-[13px] text-slate-500">{placeholder}</span>;
  }

  return (
    <Select value={selectValue} onValueChange={handleChange} disabled={blocked}>
      <SelectTrigger
        id={id}
        className={cn(
          "h-11 sm:h-9 w-full min-w-0 text-left [&>span]:line-clamp-none",
          triggerClassName,
        )}
        data-testid={testId}
      >
        {triggerContent}
      </SelectTrigger>
      <SelectContent className="max-h-[min(320px,50dvh)] min-w-[var(--radix-select-trigger-width)]">
        {allowEmpty && (
          <SelectItem value={EMPTY_VALUE} textValue={placeholder} className="text-[13px]">
            <span className="text-slate-500">{placeholder}</span>
          </SelectItem>
        )}
        {zones.map((zone) => (
          <SelectItem
            key={zone.iana}
            value={zone.iana}
            textValue={`${zone.name} ${zone.gmt}`}
            className="py-1.5"
          >
            <TimezoneOptionLabel name={zone.name} gmt={zone.gmt} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

TimezoneSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  allowEmpty: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  testId: PropTypes.string,
  triggerClassName: PropTypes.string,
  placeholder: PropTypes.string,
};

export function TimezoneInlineLabel({ iana, className }) {
  const { displayFor, ensureDisplay, loading } = useCampaignTimezoneCatalog();
  const [row, setRow] = useState(() => displayFor(iana));

  useEffect(() => {
    setRow(displayFor(iana));
  }, [iana, displayFor]);

  useEffect(() => {
    if (!iana || row || loading) return;
    ensureDisplay(iana).then(setRow);
  }, [iana, row, loading, ensureDisplay]);

  if (!iana || !row) return null;
  return (
    <span className={cn("inline-flex flex-wrap items-baseline gap-x-1.5", className)}>
      <span>{row.name}</span>
      {row.gmt ? (
        <span className="text-slate-400 text-[12px] font-normal">{row.gmt}</span>
      ) : null}
    </span>
  );
}

TimezoneInlineLabel.propTypes = {
  iana: PropTypes.string,
  className: PropTypes.string,
};
